import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';

function SelectApprenticeEmployeeTable({ onSelectionChange }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [payrollChecked, setPayrollChecked] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchApprentices = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/get-apprentice-employees`
        );
        const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setRows(data);
      } catch (e) {
        if (e.response && e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error',
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        } else {
          setNotify({
            isOpen: true,
            message: 'Failed to fetch apprentice employees',
            type: 'error',
          });
        }
      }
    };

    fetchApprentices();
  }, [navigate]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllPayroll = (event) => {
    const isChecked = event.target.checked;
    const newState = {};
    rows.forEach((row) => {
      newState[row.employee_id] = isChecked;
    });
    setPayrollChecked(newState);
    if (onSelectionChange) {
      const selected = rows
        .filter((row) => newState[row.employee_id])
        .map((row) => ({
          employee_id: row.employee_id,
          first_name: row.first_name,
          last_name: row.last_name,
          employee_code: row.employee_code,
        }));
      onSelectionChange(selected);
    }
  };

  const handlePayrollChange = (event, empId) => {
    setPayrollChecked((prev) => {
      const updated = {
        ...prev,
        [empId]: event.target.checked,
      };
      if (onSelectionChange) {
        const selected = rows
          .filter((row) => updated[row.employee_id])
          .map((row) => ({
            employee_id: row.employee_id,
            first_name: row.first_name,
            last_name: row.last_name,
            employee_code: row.employee_code,
          }));
        onSelectionChange(selected);
      }
      return updated;
    });
  };

  const payrollSelectedCount = Object.values(payrollChecked).filter(Boolean).length;
  const allPayrollSelected = rows.length > 0 && payrollSelectedCount === rows.length;
  const somePayrollSelected = payrollSelectedCount > 0 && payrollSelectedCount < rows.length;

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: '100%', mb: 0 }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          mt: '4px',
          mb: 0,
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          overflow: 'hidden',
        }}
      >
        <Toolbar
          sx={{
            pl: 1.5,
            pr: 1.5,
            py: 0.5,
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            minHeight: '40px !important',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#424242',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Select Apprentices
            <Typography
              component="span"
              sx={{
                fontSize: '0.8rem',
                color: '#757575',
                fontWeight: 400,
                ml: 0.75,
              }}
            >
              ({rows.length})
            </Typography>
          </Typography>

          <TablePagination
            rowsPerPageOptions={[50, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              border: 'none',
              backgroundColor: 'transparent',
              margin: 0,
              padding: 0,
              minHeight: '32px',
              '& .MuiTablePagination-toolbar': {
                minHeight: '32px',
                paddingLeft: 0,
                paddingRight: 0,
                justifyContent: 'flex-end',
                paddingTop: 0,
                paddingBottom: 0,
              },
              '& .MuiTablePagination-spacer': {
                display: 'none',
              },
              '& .MuiTablePagination-selectLabel': {
                fontSize: '0.75rem',
                color: '#616161',
                margin: 0,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.75rem',
                color: '#616161',
                margin: 0,
                marginLeft: 1,
              },
              '& .MuiTablePagination-select': {
                fontSize: '0.75rem',
                paddingTop: '2px',
                paddingBottom: '2px',
              },
              '& .MuiTablePagination-actions': {
                marginLeft: 1,
                '& .MuiIconButton-root': {
                  padding: '4px',
                },
              },
            }}
          />
        </Toolbar>

        <TableContainer sx={{ maxHeight: 460 }}>
          <Table stickyHeader sx={{ minWidth: 600 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  align="left"
                  padding="checkbox"
                  sx={{
                    background: '#f8fafc',
                    color: '#1565c0',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderBottom: '2px solid #e3f2fd',
                    py: 0.75,
                    width: 100,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, lineHeight: 1 }}>
                    <Checkbox
                      size="small"
                      indeterminate={somePayrollSelected && !allPayrollSelected}
                      checked={allPayrollSelected}
                      onChange={handleSelectAllPayroll}
                      sx={{
                        p: 0.25,
                        color: '#0b418e',
                        '&.Mui-checked': { color: '#0b418e' },
                        '&.MuiCheckbox-indeterminate': { color: '#0b418e' },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: '#1565c0', fontSize: '0.75rem' }}
                    >
                      Stipend 
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    background: '#f8fafc',
                    color: '#1565c0',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderBottom: '2px solid #e3f2fd',
                    py: 0.75,
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    background: '#f8fafc',
                    color: '#1565c0',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderBottom: '2px solid #e3f2fd',
                    py: 0.75,
                  }}
                >
                  Intern/Apprentice Code
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isSelected = payrollChecked[row.employee_id] || false;
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.employee_id}
                      sx={{
                        backgroundColor: isSelected
                          ? '#f3f8ff'
                          : index % 2 === 0
                          ? '#fafafa'
                          : '#fff',
                        '&:hover': {
                          backgroundColor: isSelected ? '#e8f1fc' : '#f5f5f5',
                        },
                        '& td': {
                          borderBottom: '1px solid #e8e8e8',
                          py: 0.75,
                        },
                        '&:last-child td': {
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <TableCell align="left" padding="checkbox" sx={{ width: 100 }}>
                        <Checkbox
                          size="small"
                          checked={payrollChecked[row.employee_id] || false}
                          onChange={(event) =>
                            handlePayrollChange(event, row.employee_id)
                          }
                          sx={{
                            p: 0.25,
                            color: '#bdbdbd',
                            '&.Mui-checked': { color: '#4caf50' },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          fontWeight: isSelected ? 500 : 400,
                          color: isSelected ? '#0b418e' : '#424242',
                          fontSize: '0.8rem',
                        }}
                      >
                        {`${row.first_name || ''} ${row.last_name || ''}`.trim()}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          color: '#757575',
                          fontSize: '0.8rem',
                        }}
                      >
                        {row.employee_code}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default SelectApprenticeEmployeeTable;

